import React, { useState } from "react";
import { Modal, Button } from "antd";
import { FcGoogle } from "react-icons/fc";
import {
    BellOutlined,
    CalendarOutlined,
    CheckCircleFilled,
    SyncOutlined,
} from "@ant-design/icons";
import { googleApi } from "../../services/apis/google.api";
import AuthUtils from "../../utils/auth.utils";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import { useAppDispatch } from "../../hooks/hooks";
import calendarIcon from "../../assets/calendar.png";
import "./GoogleCalendarConnectModal.scss";

interface Props {
    visible: boolean;
    onClose: () => void;
}

const calendarBenefits = [
    {
        icon: <CalendarOutlined />,
        title: "Calendar invites",
        description:
            "Send audit invites to every participant as soon as a session is scheduled.",
    },
    {
        icon: <BellOutlined />,
        title: "Smart reminders",
        description:
            "Nudge participants 24 hours and 1 hour before the audit starts.",
    },
    {
        icon: <SyncOutlined />,
        title: "Two-way updates",
        description: "Keep IMPACT aligned when schedules change in Google Calendar.",
    },
];

/**
 * GoogleCalendarConnectModal
 * --------------------------------------------------
 * Pops up once the user is inside the app (after local-auth) and
 * encourages them to link their Google Calendar so our backend
 * can push audit / task events. The modal is purely an informative
 * CTA: clicking the primary button mints a single-use connect token
 * and jumps to `/api/google/connect` with it, which starts the OAuth
 * 2.0 flow handled by the server. The login token never goes in that
 * URL.
 */
const GoogleCalendarConnectModal: React.FC<Props> = ({ visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    const handleConnect = async () => {
        setLoading(true);
        try {
            const connectToken = await googleApi.getConnectToken(
                AuthUtils.getHeaders()
            );
            window.location.href = googleApi.connectUrl(connectToken);
            setLoading(false);
        } catch (error) {
            console.error("Error connecting to Google Calendar:", error);
            minimalToast({
                dispatch,
                title: "Connection Error",
                message: "An error occurred while trying to connect to Google Calendar",
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
                type: NotificationSeverity.ERROR,
            });
            setLoading(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            centered
            width={520}
            destroyOnHidden
            footer={null}
            wrapClassName="gcal-connect-modal gcal-connect-modal--refined"
            closable
        >
            <div className="gcal-connect-modal__hero">
                <div className="gcal-connect-modal__hero-mesh" />
                <div className="gcal-connect-modal__eyebrow">
                    Recommended setup
                </div>
                <div className="gcal-connect-modal__illustration">
                    <img src={calendarIcon} alt="" aria-hidden />
                </div>
                <h2 className="gcal-connect-modal__title">
                    Sync your audit schedule
                </h2>
                <p className="gcal-connect-modal__lead">
                    Connect Google Calendar once and let IMPACT handle audit
                    invites, reminders, and schedule changes automatically.
                </p>
            </div>

            <div className="gcal-connect-modal__body">
                <div className="gcal-connect-modal__summary">
                    <CheckCircleFilled />
                    <span>
                        Takes less than a minute. You can disconnect calendar
                        access from settings later.
                    </span>
                </div>

                <ul className="gcal-connect-modal__benefits">
                    {calendarBenefits.map((benefit) => (
                        <li
                            className="gcal-connect-modal__benefit"
                            key={benefit.title}
                        >
                            <span className="gcal-connect-modal__benefit-icon">
                                {benefit.icon}
                            </span>
                            <span className="gcal-connect-modal__benefit-copy">
                                <strong>{benefit.title}</strong>
                                <span>{benefit.description}</span>
                            </span>
                        </li>
                    ))}
                </ul>

                <div className="gcal-connect-modal__actions">
                    <Button
                        type="primary"
                        icon={<FcGoogle size={20} />}
                        loading={loading}
                        onClick={handleConnect}
                        size="large"
                        className="gcal-connect-modal__primary-btn"
                        block
                    >
                        Connect Google Calendar
                    </Button>
                    <Button
                        type="text"
                        onClick={onClose}
                        size="large"
                        className="gcal-connect-modal__secondary-btn"
                        block
                    >
                        Not now
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default GoogleCalendarConnectModal;
